import { useState, useMemo, useEffect } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useCollection } from '@cloudscape-design/collection-hooks'
import Alert from '@cloudscape-design/components/alert'
import Badge from '@cloudscape-design/components/badge'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import CollectionPreferences from '@cloudscape-design/components/collection-preferences'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Container from '@cloudscape-design/components/container'
import Grid from '@cloudscape-design/components/grid'
import FormField from '@cloudscape-design/components/form-field'
import Header from '@cloudscape-design/components/header'
import Icon from '@cloudscape-design/components/icon'
import Input from '@cloudscape-design/components/input'
import Modal from '@cloudscape-design/components/modal'
import Pagination from '@cloudscape-design/components/pagination'
import Select from '@cloudscape-design/components/select'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Spinner from '@cloudscape-design/components/spinner'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Table from '@cloudscape-design/components/table'
import Textarea from '@cloudscape-design/components/textarea'
import Cards from '@cloudscape-design/components/cards'
import Link from '@cloudscape-design/components/link'
import Popover from '@cloudscape-design/components/popover'
import DatePicker from '@cloudscape-design/components/date-picker'
import TimeInput from '@cloudscape-design/components/time-input'
import type { PublishStatus, SeoMetadata, AnyContentType, PublishQueueItem, PlatformName } from '@/lib/types'
import {
  usePublishQueueFull,
  useApproveItem,
  useRejectItem,
  useEditItem,
  useScheduleItem,
  useDeleteItem,
  useClearQueue,
} from '@/hooks/useContent'
import { useAccount } from '@/hooks/useAccount'
import { formatDate } from '@/utils/format'
import { getContentTypeLabel, getContentTypeColor, getContentTypePlatform, getItemTitle } from '@/utils/contentType'
import { getPlatformIcon } from '@/utils/platform'

const CONTENT_TYPE_TO_PLATFORM: Record<string, string> = {
  email_outreach: 'gmail',
  outreach_email: 'gmail',
  facebook_post: 'facebook',
  facebook_ad: 'facebook',
  linkedin_post: 'linkedin',
  twitter_post: 'twitter',
  gbp_post: 'google',
  blog_seo: 'medium',
  social_media: 'facebook',
  ad_copy: 'facebook',
}

const isEmailType = (ct: string | undefined) =>
  ct === 'email_outreach' || ct === 'outreach_email'

// ─── Runtime shape ─────────────────────────────────────────────────────────────

// GraphQL returns camelCase id instead of _id
interface QueueRow extends Omit<PublishQueueItem, '_id'> {
  id: string
  runId?: string
  contentType?: AnyContentType
  createdAt?: string
  approvedAt?: string
  scheduledFor?: string
  toEmail?: string
  recipientName?: string
  targetPlatform?: string
  seoMetadata?: SeoMetadata
  errorMessage?: string
  publishResults?: Array<{ platform: string; success: boolean; platformUrl?: string; platformPostId?: string; error?: string }>
}

// Badge only supports blue | grey | green | red; map orange → red
function toBadgeColor(color: 'blue' | 'green' | 'red' | 'grey' | 'orange'): 'blue' | 'green' | 'red' | 'grey' {
  return color === 'orange' ? 'red' : color
}

// ─── Status mappings ───────────────────────────────────────────────────────────

const STATUS_INDICATOR: Record<PublishStatus, 'warning' | 'in-progress' | 'success' | 'error' | 'pending' | 'stopped'> = {
  draft: 'pending',
  pending: 'pending',
  waiting_approval: 'pending',
  approved: 'in-progress',
  publishing: 'in-progress',
  published: 'success',
  failed: 'error',
  scheduled: 'pending',
  rejected: 'stopped',
}

const STATUS_LABEL: Record<PublishStatus, string> = {
  draft: 'Needs Review',
  pending: 'Needs Review',
  waiting_approval: 'Needs Review',
  approved: 'Sending…',
  publishing: 'Publishing',
  published: 'Published',
  failed: 'Failed',
  scheduled: 'Scheduled',
  rejected: 'Rejected',
}

function getStatusLabel(status: PublishStatus, contentType: string | undefined): string {
  if (status === 'published' && isEmailType(contentType)) return 'Sent'
  return STATUS_LABEL[status] ?? status
}

// ─── Module-level constants ────────────────────────────────────────────────────

const PILL_STATUSES: Array<{ key: PublishStatus; label: string }> = [
  { key: 'pending', label: 'Needs Review' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'publishing', label: 'Publishing' },
  { key: 'published', label: 'Published' },
  { key: 'failed', label: 'Failed' },
]

// ─── Component ─────────────────────────────────────────────────────────────────

export default function QueueTab({ clientId }: { clientId: string }) {
  const location = useLocation()
  const { state } = location
  const navigate = useNavigate()
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const { items: allItems, loading, error } = usePublishQueueFull(clientId)
  const { approve, loading: approveLoading } = useApproveItem()
  const { reject, loading: rejectLoading } = useRejectItem()
  const { edit, loading: editLoading } = useEditItem()
  const { schedule, loading: scheduleLoading } = useScheduleItem()
  const { deleteItem, loading: deleteLoading } = useDeleteItem()
  const { clearQueue, loading: clearLoading } = useClearQueue()
  const { account } = useAccount()

  const getPlatformConnected = (contentType: string | undefined): boolean => {
    if (!contentType) return true
    const platform = CONTENT_TYPE_TO_PLATFORM[contentType]
    if (!platform) return true
    return account?.platforms?.some(p => p.platform === platform && p.connected) ?? true
  }

  // Cast once — Apollo returns camelCase id and extra camelCase fields
  const rows = (allItems as unknown as QueueRow[])

  const [statusFilter, setStatusFilter] = useState<PublishStatus | 'all'>(state?.statusFilter ?? 'all')
  const [typeFilter, setTypeFilter] = useState<string>(state?.typeFilter ?? 'all')
  // Hide published items by default — toggle adds them back into the view
  const [showPublished, setShowPublished] = useState<boolean>(state?.statusFilter === 'published')

  // Deep link from History → Queue: filter to a single campaign run
  const [searchParams, setSearchParams] = useSearchParams()
  const runIdFilter = searchParams.get('runId')

  // Clear navigation state from history entry so back-navigation doesn't re-apply stale filters
  useEffect(() => {
    if (state?.statusFilter || state?.typeFilter) {
      navigate(location.pathname + location.search, { replace: true, state: null })
    }
  }, []) // intentional empty deps — runs once on mount to clear navigation state
  const [preferences, setPreferences] = useState({
    pageSize: 15,
    visibleContent: ['title', 'platform', 'status', 'compliance', 'postLinks', 'actions'],
  })

  const [selectedItem, setSelectedItem] = useState<QueueRow | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editedContent, setEditedContent] = useState('')
  const [editedSubject, setEditedSubject] = useState('')
  const [editedBody, setEditedBody] = useState('')
  const [editedSeo, setEditedSeo] = useState<SeoMetadata | undefined>(undefined)
  const [overrideEmail, setOverrideEmail] = useState('')
  const [showScheduler, setShowScheduler] = useState(false)
  const [scheduleDate, setScheduleDate] = useState('') // YYYY/MM/DD from DatePicker
  const [scheduleTime, setScheduleTime] = useState('') // HH:MM from TimeInput
  const [showApproveConfirm, setShowApproveConfirm] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectFeedback, setRejectFeedback] = useState('')
  const [approveError, setApproveError] = useState<string | null>(null)
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<QueueRow | null>(null)
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  // Review All workflow: when active, advance through pending items via "Next →"
  const [reviewAllMode, setReviewAllMode] = useState(false)
  const [reviewAllQueue, setReviewAllQueue] = useState<QueueRow[]>([])

  const statusCounts = useMemo(() => {
    const counts: Record<PublishStatus, number> = {
      draft: 0, scheduled: 0, pending: 0, waiting_approval: 0,
      approved: 0, publishing: 0, published: 0, failed: 0, rejected: 0,
    }
    for (const item of rows) {
      const s = item.status as PublishStatus
      if (s in counts) counts[s]++
    }
    return counts
  }, [rows])

  const outstandingCount = useMemo(
    () => rows.filter(i => i.status === 'pending' || i.status === 'waiting_approval').length,
    [rows]
  )

  const queueSummary = useMemo(() => ({
    pending: rows.filter(i =>
      i.status === 'pending' || i.status === 'draft' || i.status === 'waiting_approval'
    ).length,
    published: rows.filter(i => i.status === 'published').length,
    scheduled: rows.filter(i => i.status === 'scheduled').length,
  }), [rows])

  const uniqueTypes = useMemo(() => {
    const seen = new Set<string>()
    for (const item of rows) {
      const ct = item.contentType ?? item.content_type
      if (ct) seen.add(ct)
    }
    return Array.from(seen)
  }, [rows])

  const publishedCount = statusCounts.published

  const filtered = useMemo(() => {
    let result = rows
    if (statusFilter !== 'all') {
      result = result.filter(i => i.status === statusFilter)
    } else if (!showPublished) {
      // Default view excludes published — toggle adds them back
      result = result.filter(i => i.status !== 'published')
    }
    if (typeFilter !== 'all') result = result.filter(i => (i.contentType ?? i.content_type) === typeFilter)
    if (runIdFilter) {
      result = result.filter(i => (i.runId ?? i.run_id) === runIdFilter)
    }
    return result
  }, [rows, statusFilter, typeFilter, showPublished, runIdFilter])

  const { items, collectionProps, paginationProps } = useCollection(filtered, {
    sorting: {},
    pagination: { pageSize: isMobile ? 10 : preferences.pageSize },
  })

  const openModal = (item: QueueRow) => {
    setSelectedItem(item)
    const rawContent = item.content ?? ''
    setEditedContent(rawContent)
    const ct = item.contentType ?? item.content_type
    if (isEmailType(ct)) {
      const subjectMatch = rawContent.match(/^Subject:\s*(.+)/m)
      if (subjectMatch) {
        setEditedSubject(subjectMatch[1].trim())
        const bodyStart = rawContent.indexOf('\n\n')
        setEditedBody(bodyStart !== -1 ? rawContent.slice(bodyStart + 2) : rawContent)
      } else {
        setEditedSubject(item.subject ?? '')
        setEditedBody(rawContent)
      }
    } else {
      setEditedSubject('')
      setEditedBody('')
    }
    setIsEditing(false)
    setShowScheduler(false)
    setShowRejectModal(false)
    setShowApproveConfirm(false)
    setScheduleDate('')
    setScheduleTime('')
    setRejectFeedback('')
    setApproveError(null)
    setEditedSeo(item.seoMetadata ?? item.seo_metadata)
    setOverrideEmail(item.toEmail ?? item.to_email ?? '')
  }

  const closeModal = () => {
    setSelectedItem(null)
    setShowApproveConfirm(false)
    setReviewAllMode(false)
    setReviewAllQueue([])
  }

  const advanceReviewAll = () => {
    if (reviewAllQueue.length === 0) {
      closeModal()
      return
    }
    const [next, ...rest] = reviewAllQueue
    setReviewAllQueue(rest)
    openModal(next)
  }

  const startReviewAll = () => {
    const pending = rows.filter(i =>
      i.status === 'draft' || i.status === 'waiting_approval' || i.status === 'pending'
    )
    if (pending.length === 0) return
    const [first, ...rest] = pending
    setReviewAllMode(true)
    setReviewAllQueue(rest)
    openModal(first)
  }

  const handleApprove = async () => {
    if (!selectedItem) return
    setApproveError(null)
    try {
      await approve(clientId, selectedItem.id, overrideEmail || undefined)
      if (reviewAllMode && reviewAllQueue.length > 0) {
        advanceReviewAll()
      } else {
        closeModal()
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.'
      setApproveError(msg)
    }
  }

  const handleReject = async () => {
    if (!selectedItem) return
    await reject(clientId, selectedItem.id, rejectFeedback || undefined)
    setShowRejectModal(false)
    setRejectFeedback('')
    if (reviewAllMode && reviewAllQueue.length > 0) {
      advanceReviewAll()
    } else {
      closeModal()
    }
  }

  const handleSchedule = async () => {
    if (!selectedItem || !scheduleDate) return
    const timeStr = scheduleTime || '00:00'
    const isoString = new Date(`${scheduleDate.replace(/\//g, '-')}T${timeStr}:00`).toISOString()
    await schedule(clientId, selectedItem.id, isoString)
    setShowScheduler(false)
    setScheduleDate('')
    setScheduleTime('')
  }

  const handleSaveEdits = async () => {
    if (!selectedItem) return
    const ct = selectedItem.contentType ?? selectedItem.content_type
    const newContent = isEmailType(ct)
      ? `Subject: ${editedSubject}\n\n${editedBody}`
      : editedContent
    await edit(clientId, selectedItem.id, newContent, overrideEmail || undefined)
    setIsEditing(false)
  }

  const contentChanged = (() => {
    if (!selectedItem) return false
    const ct = selectedItem.contentType ?? selectedItem.content_type
    if (!isEmailType(ct)) return editedContent !== (selectedItem.content ?? '')
    const raw = selectedItem.content ?? ''
    const subjectMatch = raw.match(/^Subject:\s*(.+)/m)
    const originalSubject = subjectMatch ? subjectMatch[1].trim() : (selectedItem.subject ?? '')
    const bodyStart = raw.indexOf('\n\n')
    const originalBody = subjectMatch
      ? (bodyStart !== -1 ? raw.slice(bodyStart + 2) : raw)
      : raw
    return editedSubject !== originalSubject || editedBody !== originalBody
  })()

  if (loading) return <Spinner />
  if (error) return <Alert type="error">{error.message}</Alert>

  const typeOptions = [
    { label: 'All types', value: 'all' },
    ...uniqueTypes.map(t => ({ label: getContentTypeLabel(t as AnyContentType), value: t })),
  ]

  const selectedTypeOption = typeOptions.find(o => o.value === typeFilter) ?? typeOptions[0]

  const columnDefinitions = [
    {
      id: 'title',
      header: 'Title',
      minWidth: 220,
      sortingField: 'title',
      cell: (item: QueueRow) => {
        const title = getItemTitle(item)
        return (
          <Box color={item.status === 'draft' ? 'text-status-warning' : 'text-body-secondary'}>
            {title}
          </Box>
        )
      },
    },
    {
      id: 'platform',
      header: 'Platform',
      width: 160,
      minWidth: 140,
      cell: (item: QueueRow) => {
        const ct = (item.contentType ?? item.content_type) as AnyContentType
        if (!ct) return <Box color="text-body-secondary">—</Box>
        const platform = getContentTypePlatform(ct) ?? (isEmailType(ct) ? 'gmail' : null)
        const label = getContentTypeLabel(ct)
        if (platform) {
          const Icon = getPlatformIcon(platform as PlatformName)
          return (
            <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
              <Icon size={20} />
              <Box fontSize="body-s">{label}</Box>
            </SpaceBetween>
          )
        }
        return <Badge color={toBadgeColor(getContentTypeColor(ct))}>{label}</Badge>
      },
    },
    {
      id: 'status',
      header: 'Status',
      width: 260,
      minWidth: 200,
      cell: (item: QueueRow) => {
        const scheduledDate = item.scheduledFor ?? item.scheduled_for
        const indicator = (
          <StatusIndicator type={STATUS_INDICATOR[item.status]}>
            {getStatusLabel(item.status, item.contentType ?? item.content_type)}
          </StatusIndicator>
        )
        if (item.status === 'scheduled' && scheduledDate) {
          return (
            <SpaceBetween size="xxs" direction="vertical">
              {indicator}
              <Box fontSize="body-s" color="text-body-secondary">
                {formatDate(scheduledDate)}
              </Box>
            </SpaceBetween>
          )
        }
        if (item.status !== 'failed') return indicator
        const reason =
          (item.publishResults ?? []).find(r => !r.success)?.error
          ?? item.errorMessage
          ?? 'Publish failed — open the item for details.'
        return (
          <SpaceBetween size="xxs">
            {indicator}
            <Popover
              dismissButton={false}
              position="top"
              size="medium"
              triggerType="custom"
              content={<Box variant="p">{reason}</Box>}
            >
              <Box color="text-status-error" fontSize="body-s">
                {reason.length > 60 ? `${reason.slice(0, 60)}…` : reason}
              </Box>
            </Popover>
          </SpaceBetween>
        )
      },
    },
    {
      id: 'compliance',
      header: 'Compliance',
      width: 130,
      minWidth: 110,
      cell: (item: QueueRow) => {
        if (!item.compliance) return <Box color="text-body-secondary">—</Box>
        return item.compliance.approved
          ? <StatusIndicator type="success">OK</StatusIndicator>
          : <StatusIndicator type="warning">Issues</StatusIndicator>
      },
    },
    {
      id: 'postLinks',
      header: 'Post Links',
      width: 160,
      minWidth: 140,
      cell: (item: QueueRow) => {
        const results = item.publishResults ?? []
        const links = results.filter(r => r.success && r.platformUrl)
        if (!links.length) return <Box color="text-body-secondary">—</Box>
        return (
          <SpaceBetween direction="horizontal" size="xxs">
            {links.map(r => (
              <Link key={r.platform} href={r.platformUrl} external fontSize="body-s">
                {r.platform}
              </Link>
            ))}
          </SpaceBetween>
        )
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      width: 130,
      minWidth: 130,
      cell: (item: QueueRow) => (
        <SpaceBetween direction="horizontal" size="s" alignItems="center">
          <Button
            variant="inline-icon"
            iconSvg={<Icon name="view-full" variant="link" />}
            ariaLabel="Review"
            onClick={(e) => {
              e.stopPropagation()
              openModal(item)
            }}
          />
          {item.status === 'failed' && (
            <Button
              variant="inline-icon"
              iconSvg={<Icon name="refresh" variant="success" />}
              ariaLabel="Retry"
              loading={approveLoading}
              disabled={approveLoading}
              onClick={(e) => {
                e.stopPropagation()
                void approve(clientId, item.id)
              }}
            />
          )}
          <Button
            variant="inline-icon"
            iconSvg={<Icon name="remove" variant="error" />}
            ariaLabel="Delete"
            onClick={(e) => {
              e.stopPropagation()
              setDeleteConfirmItem(item)
            }}
          />
        </SpaceBetween>
      ),
    },
  ]

  const visibleColumns = preferences.visibleContent

  const clearRunIdFilter = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('runId')
    setSearchParams(next)
  }


  return (
    <SpaceBetween size="m">
      {runIdFilter && (
        <Alert
          type="info"
          dismissible
          onDismiss={clearRunIdFilter}
          statusIconAriaLabel="Info"
        >
          Showing items from one campaign run.{' '}
          <Link onFollow={clearRunIdFilter}>Show all</Link>
        </Alert>
      )}

      <Container>
        {/* Grid (12-col) holds columns at fixed widths on all screen sizes; ColumnLayout collapses on narrow viewports */}
        <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
          <Box>
            <Box variant="awsui-key-label">Awaiting review</Box>
            <Box fontSize={isMobile ? 'heading-xl' : 'display-l'} fontWeight="bold">{queueSummary.pending}</Box>
          </Box>
          <Box>
            <Box variant="awsui-key-label">Published</Box>
            <Box fontSize={isMobile ? 'heading-xl' : 'display-l'} fontWeight="bold">{queueSummary.published}</Box>
          </Box>
          <Box>
            <Box variant="awsui-key-label">Scheduled</Box>
            <Box fontSize={isMobile ? 'heading-xl' : 'display-l'} fontWeight="bold">{queueSummary.scheduled}</Box>
          </Box>
        </Grid>
      </Container>

      {isMobile ? (
        <SpaceBetween direction="vertical" size="xs">
          {/* Horizontal-scroll pill bar: overflowX + flexShrink:0 have no Cloudscape equivalent */}
          <div style={{ overflowX: 'auto', display: 'flex', gap: '4px', paddingBottom: '2px' }}>
            {PILL_STATUSES.map(({ key, label }) => (
              <span key={key} style={{ flexShrink: 0 }}>
                <Button
                  variant="link"
                  onClick={() => setStatusFilter(prev => prev === key ? 'all' : key)}
                >
                  <SpaceBetween direction="horizontal" size="xxs">
                    <Box variant="span">{label}</Box>
                    <Badge color={statusFilter === key ? 'blue' : 'grey'}>{String(statusCounts[key])}</Badge>
                  </SpaceBetween>
                </Button>
              </span>
            ))}
          </div>
          <Select
            selectedOption={selectedTypeOption}
            onChange={({ detail }) => setTypeFilter(detail.selectedOption.value ?? 'all')}
            options={typeOptions}
          />
        </SpaceBetween>
      ) : (
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          {PILL_STATUSES.map(({ key, label }) => (
            <Button
              key={key}
              variant="link"
              onClick={() => setStatusFilter(prev => prev === key ? 'all' : key)}
            >
              <SpaceBetween direction="horizontal" size="xxs">
                <Box variant="span">{label}</Box>
                <Badge color={statusFilter === key ? 'blue' : 'grey'}>{String(statusCounts[key])}</Badge>
              </SpaceBetween>
            </Button>
          ))}
          <Box float="right">
            <Select
              selectedOption={selectedTypeOption}
              onChange={({ detail }) => setTypeFilter(detail.selectedOption.value ?? 'all')}
              options={typeOptions}
            />
          </Box>
        </SpaceBetween>
      )}

      {isMobile ? (
        <Cards
          {...collectionProps}
          items={items}
          cardsPerRow={[{ cards: 1 }]}
          header={
            <Header
              counter={`(${filtered.length})`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {outstandingCount > 1 && (
                    <Button variant="primary" onClick={startReviewAll}>
                      {`Review All (${outstandingCount})`}
                    </Button>
                  )}
                  {publishedCount > 0 && (
                    <Button
                      variant={showPublished ? 'primary' : 'normal'}
                      onClick={() => setShowPublished(prev => !prev)}
                    >
                      {showPublished ? 'Hide Published' : `Show Published (${publishedCount})`}
                    </Button>
                  )}
                  <Button
                    variant="icon"
                    iconName="remove"
                    ariaLabel="Clear Queue"
                    disabled={rows.length === 0}
                    onClick={() => setShowClearConfirm(true)}
                  />
                </SpaceBetween>
              }
            >
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <Box variant="span">Queue</Box>
                {outstandingCount > 0 && (
                  <Badge color="blue">{`${outstandingCount} awaiting review`}</Badge>
                )}
              </SpaceBetween>
            </Header>
          }
          cardDefinition={{
            header: (item: QueueRow) => (
              <Box color={item.status === 'draft' ? 'text-status-warning' : 'text-body-secondary'}>
                {getItemTitle(item)}
              </Box>
            ),
            sections: [
              {
                id: 'platformStatus',
                content: (item: QueueRow) => {
                  const ct = (item.contentType ?? item.content_type) as AnyContentType
                  const platform = ct
                    ? getContentTypePlatform(ct) ?? (isEmailType(ct) ? 'gmail' : null)
                    : null
                  return (
                    <ColumnLayout columns={2} borders="none">
                      <SpaceBetween size="xxs">
                        <Box variant="awsui-key-label">Platform</Box>
                        {platform ? (
                          <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
                            {(() => {
                              const PIcon = getPlatformIcon(platform as PlatformName)
                              return <PIcon size={20} />
                            })()}
                            <Box fontSize="body-s">{getContentTypeLabel(ct)}</Box>
                          </SpaceBetween>
                        ) : ct ? (
                          <Badge color={toBadgeColor(getContentTypeColor(ct))}>{getContentTypeLabel(ct)}</Badge>
                        ) : (
                          <Box color="text-body-secondary">—</Box>
                        )}
                      </SpaceBetween>
                      <SpaceBetween size="xxs">
                        <Box variant="awsui-key-label">Status</Box>
                        <StatusIndicator type={STATUS_INDICATOR[item.status]}>
                          {getStatusLabel(item.status, item.contentType ?? item.content_type)}
                        </StatusIndicator>
                      </SpaceBetween>
                    </ColumnLayout>
                  )
                },
              },
              {
                id: 'actions',
                content: (item: QueueRow) => (
                  <SpaceBetween direction="horizontal" size="s" alignItems="center">
                    <Button variant="inline-link" onClick={() => openModal(item)}>Review</Button>
                    {item.status === 'failed' && (
                      <Button
                        variant="inline-link"
                        loading={approveLoading}
                        onClick={() => void approve(clientId, item.id)}
                      >
                        Retry
                      </Button>
                    )}
                    <Button variant="inline-link" onClick={() => setDeleteConfirmItem(item)}>Delete</Button>
                  </SpaceBetween>
                ),
              },
            ],
          }}
          empty={
            <Box textAlign="center" padding="l">
              No items in queue. Run a campaign to generate content.
            </Box>
          }
        />
      ) : (
      <Table<QueueRow>
        {...collectionProps}
        items={items}
        onRowClick={({ detail }) => openModal(detail.item)}
        resizableColumns
        stripedRows
        header={
          <Header
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {outstandingCount > 1 && (
                  <Button
                    variant="primary"
                    onClick={startReviewAll}
                  >
                    {`Review All (${outstandingCount}) →`}
                  </Button>
                )}
                {publishedCount > 0 && (
                  <Button
                    variant={showPublished ? 'primary' : 'normal'}
                    onClick={() => setShowPublished(prev => !prev)}
                  >
                    {showPublished ? `Hide Published (${publishedCount})` : `Show Published (${publishedCount})`}
                  </Button>
                )}
                <Button
                  variant="normal"
                  iconName="remove"
                  disabled={rows.length === 0}
                  onClick={() => setShowClearConfirm(true)}
                >
                  Clear Queue
                </Button>
                <CollectionPreferences
                  title="Preferences"
                  confirmLabel="Confirm"
                  cancelLabel="Cancel"
                  preferences={preferences}
                  onConfirm={({ detail }) =>
                    setPreferences({
                      pageSize: detail.pageSize ?? 15,
                      visibleContent: (detail.visibleContent as string[]) ?? preferences.visibleContent,
                    })
                  }
                  pageSizePreference={{
                    title: 'Page size',
                    options: [
                      { value: 10, label: '10 items' },
                      { value: 15, label: '15 items' },
                      { value: 25, label: '25 items' },
                      { value: 50, label: '50 items' },
                    ],
                  }}
                  visibleContentPreference={{
                    title: 'Select visible columns',
                    options: [
                      {
                        label: 'Columns',
                        options: [
                          { id: 'title', label: 'Title' },
                          { id: 'platform', label: 'Platform' },
                          { id: 'status', label: 'Status' },
                          { id: 'compliance', label: 'Compliance' },
                          { id: 'postLinks', label: 'Post Links' },
                        ],
                      },
                    ],
                  }}
                />
              </SpaceBetween>
            }
          >
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Box variant="span">Queue</Box>
              {outstandingCount > 0 && (
                <Badge color="blue">{`${outstandingCount} awaiting review`}</Badge>
              )}
            </SpaceBetween>
          </Header>
        }
        empty={
          <Box textAlign="center" padding="l">
            No items in queue. Run a campaign to generate content.
          </Box>
        }
        columnDefinitions={columnDefinitions.filter(c => c.id === 'actions' || visibleColumns.includes(c.id))}
      />
      )}

      <Pagination {...paginationProps} />

      {selectedItem && (
        <Modal
          size="large"
          visible
          onDismiss={closeModal}
          header={
            <Header>
              {getContentTypeLabel((selectedItem.contentType ?? selectedItem.content_type) as AnyContentType)}
            </Header>
          }
          footer={(() => {
            const ct = selectedItem.contentType ?? selectedItem.content_type
            const platformConnected = getPlatformConnected(ct)
            const isEmail = isEmailType(ct)
            const hasRecipient = !!(overrideEmail || selectedItem.toEmail || selectedItem.to_email)
            const canSend = platformConnected && (!isEmail || hasRecipient)
            const showActions = (selectedItem.status === 'draft' || selectedItem.status === 'pending')
            return (
              <SpaceBetween direction="vertical" size="s">
                {approveError && (
                  <Alert type="error" statusIconAriaLabel="Error" dismissible onDismiss={() => setApproveError(null)}>
                    {approveError}
                  </Alert>
                )}
                {showActions && !platformConnected && (
                  <Alert type="warning" statusIconAriaLabel="Warning">
                    The platform for this content type is not connected. Go to{' '}
                    <Link href="/settings?tab=platforms" onFollow={e => { e.preventDefault(); navigate('/settings?tab=platforms') }}>
                      Settings &gt; Platforms
                    </Link>{' '}
                    to connect it before publishing.
                  </Alert>
                )}
                {showActions && isEmail && platformConnected && !hasRecipient && (
                  <Alert type="warning" statusIconAriaLabel="Warning">
                    Enter a recipient email in the &quot;Send to&quot; field above before sending.
                  </Alert>
                )}
                <Box>
                  <Box float="left">
                    {(selectedItem.status === 'draft' || selectedItem.status === 'pending' || selectedItem.status === 'failed') && (
                      <Button variant="normal" onClick={() => setShowRejectModal(true)}>Reject</Button>
                    )}
                  </Box>
                  <Box float="right">
                    <SpaceBetween direction="horizontal" size="s">
                      {reviewAllMode && reviewAllQueue.length > 0 && (
                        <Button variant="normal" onClick={advanceReviewAll}>
                          {`Next → (${reviewAllQueue.length} left)`}
                        </Button>
                      )}
                      {isEditing && contentChanged && (
                        <Button variant="normal" loading={editLoading} onClick={handleSaveEdits}>
                          Save edits
                        </Button>
                      )}
                      {showActions && (
                        <SpaceBetween direction="horizontal" size="s">
                          <Button
                            variant="normal"
                            iconName="calendar"
                            disabled={!platformConnected}
                            onClick={() => setShowScheduler(prev => !prev)}
                          >
                            Schedule
                          </Button>
                          {showApproveConfirm ? (
                            <SpaceBetween direction="horizontal" size="xs">
                              <Button variant="link" onClick={() => setShowApproveConfirm(false)}>
                                Cancel
                              </Button>
                              <Button
                                variant="primary"
                                loading={approveLoading}
                                disabled={!canSend}
                                onClick={async () => {
                                  await handleApprove()
                                  setShowApproveConfirm(false)
                                }}
                              >
                                {isEmail ? 'Confirm & Send' : 'Confirm & Publish'}
                              </Button>
                            </SpaceBetween>
                          ) : (
                            <Button
                              variant="primary"
                              disabled={!canSend}
                              onClick={() => setShowApproveConfirm(true)}
                            >
                              {isEmail ? 'Send Email' : 'Publish Now'}
                            </Button>
                          )}
                        </SpaceBetween>
                      )}
                    </SpaceBetween>
                  </Box>
                </Box>
              </SpaceBetween>
            )
          })()}
        >
          <SpaceBetween size="m">
            <ColumnLayout columns={4}>
              <Box>
                <Box variant="awsui-key-label">Status</Box>
                <StatusIndicator type={STATUS_INDICATOR[selectedItem.status]}>
                  {getStatusLabel(selectedItem.status, selectedItem.contentType ?? selectedItem.content_type)}
                </StatusIndicator>
              </Box>
              <Box>
                <Box variant="awsui-key-label">Created</Box>
                <Box>
                  {selectedItem.createdAt ?? selectedItem.created_at
                    ? formatDate((selectedItem.createdAt ?? selectedItem.created_at)!)
                    : '—'}
                </Box>
              </Box>
              <Box>
                <Box variant="awsui-key-label">Approved</Box>
                <Box>
                  {selectedItem.approvedAt ?? selectedItem.approved_at
                    ? formatDate((selectedItem.approvedAt ?? selectedItem.approved_at)!)
                    : '—'}
                </Box>
              </Box>
              {(selectedItem.runId ?? selectedItem.run_id) && (
                <Box>
                  <Box variant="awsui-key-label">Source run</Box>
                  <Link onFollow={() => {
                    void navigate(`/workspace/clients/${clientId}/history`)
                  }}>
                    View run →
                  </Link>
                </Box>
              )}
            </ColumnLayout>

            {selectedItem.compliance && !selectedItem.compliance.approved && selectedItem.compliance.issues.length > 0 && (() => {
              // GraphQL returns camelCase `suggestedFixes`; type uses snake_case — accept either
              const compliance = selectedItem.compliance as { suggested_fixes?: string[]; suggestedFixes?: string[] }
              const fixes = compliance.suggested_fixes ?? compliance.suggestedFixes
              const firstFix = Array.isArray(fixes) && fixes.length > 0 ? fixes[0] : null
              return (
                <Alert type="warning" header="Compliance issues">
                  <SpaceBetween size="s">
                    <SpaceBetween size="xxs">
                      {selectedItem.compliance.issues.map((issue) => (
                        <Box key={issue} variant="p">{issue}</Box>
                      ))}
                    </SpaceBetween>
                    {firstFix && (
                      <SpaceBetween size="xxs">
                        <Box variant="awsui-key-label">Suggested fix</Box>
                        <Box variant="p">{firstFix}</Box>
                        <Button
                          variant="inline-link"
                          onClick={() => {
                            setEditedContent(firstFix)
                            setIsEditing(true)
                          }}
                        >
                          Apply fix
                        </Button>
                      </SpaceBetween>
                    )}
                  </SpaceBetween>
                </Alert>
              )
            })()}

            {selectedItem.status === 'published' && (selectedItem.publishResults ?? []).filter(r => r.success && r.platformUrl).length > 0 && (
              <Container header={<Header variant="h3">Published to</Header>}>
                <SpaceBetween direction="horizontal" size="s">
                  {(selectedItem.publishResults ?? [])
                    .filter(r => r.success && r.platformUrl)
                    .map(r => (
                      <Link key={r.platform} href={r.platformUrl} external>
                        {r.platform}
                      </Link>
                    ))}
                </SpaceBetween>
              </Container>
            )}

            {selectedItem.status === 'failed' && (() => {
              const failedResults = (selectedItem.publishResults ?? []).filter(r => !r.success)
              if (failedResults.length > 0) {
                return (
                  <SpaceBetween direction="vertical" size="xs">
                    {failedResults.map(r => (
                      <Alert key={r.platform} type="error" header={`Failed to publish to ${r.platform}`}>
                        {r.error ?? 'An unknown error occurred.'}
                      </Alert>
                    ))}
                  </SpaceBetween>
                )
              }
              return (
                <Alert type="error" header="Publish failed">
                  This post could not be published. Check your platform connection and try again.
                </Alert>
              )
            })()}

            <Container
              header={
                <Header
                  variant="h3"
                  actions={
                    <Button variant="normal" onClick={() => setIsEditing(prev => !prev)}>
                      {isEditing ? 'Preview' : 'Edit'}
                    </Button>
                  }
                >
                  Content
                </Header>
              }
            >
              {isEmailType(selectedItem.contentType ?? selectedItem.content_type) ? (
                isEditing ? (
                  <SpaceBetween size="s">
                    <FormField label="Subject">
                      <Input value={editedSubject} onChange={({ detail }) => setEditedSubject(detail.value)} />
                    </FormField>
                    <FormField label="Body">
                      <Textarea value={editedBody} onChange={({ detail }) => setEditedBody(detail.value)} rows={10} />
                    </FormField>
                  </SpaceBetween>
                ) : (
                  <SpaceBetween size="m">
                    <Box>
                      <Box variant="awsui-key-label">Subject</Box>
                      <Box padding={{ top: 'xxs' }}>{editedSubject || '(no subject)'}</Box>
                    </Box>
                    <Box>
                      <Box variant="awsui-key-label">Body</Box>
                      <Box padding={{ top: 'xxs' }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{editedBody}</ReactMarkdown>
                      </Box>
                    </Box>
                  </SpaceBetween>
                )
              ) : (
                isEditing ? (
                  <SpaceBetween size="xs">
                    <Textarea value={editedContent} onChange={({ detail }) => setEditedContent(detail.value)} rows={15} />
                    {(() => {
                      const ct = selectedItem.contentType ?? selectedItem.content_type ?? ''
                      const limits: Record<string, number> = {
                        twitter: 280,
                        linkedin: 3000,
                        facebook: 63206,
                        instagram: 2200,
                      }
                      const matched = Object.entries(limits).find(([key]) => ct.includes(key))
                      if (!matched) return null
                      const limit = matched[1]
                      const current = editedContent.length
                      const pct = current / limit
                      return (
                        <Box
                          fontSize="body-s"
                          color={pct > 0.9 ? 'text-status-error' : 'text-body-secondary'}
                        >
                          {`${current.toLocaleString()} / ${limit.toLocaleString()} characters`}
                        </Box>
                      )
                    })()}
                  </SpaceBetween>
                ) : (() => {
                  let parsed: Record<string, unknown> | null = null
                  try { parsed = JSON.parse(editedContent) as Record<string, unknown> } catch { /* not JSON */ }
                  if (parsed && typeof parsed === 'object') {
                    return (
                      <SpaceBetween size="m">
                        {typeof parsed.content === 'string' && (
                          <Box variant="p">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{parsed.content}</ReactMarkdown>
                          </Box>
                        )}
                        {typeof parsed.cta === 'string' && parsed.cta.length > 0 && (
                          <Box>
                            <Badge color="blue">{`CTA: ${parsed.cta}`}</Badge>
                          </Box>
                        )}
                        {Array.isArray(parsed.hashtags) && (parsed.hashtags as string[]).length > 0 && (
                          <SpaceBetween direction="horizontal" size="xxs">
                            {(parsed.hashtags as string[]).map(tag => (
                              <Badge key={tag} color="grey">{tag.startsWith('#') ? tag : `#${tag}`}</Badge>
                            ))}
                          </SpaceBetween>
                        )}
                        {typeof parsed.character_count === 'number' && (
                          <Box color="text-body-secondary" fontSize="body-s">
                            {parsed.character_count} characters
                          </Box>
                        )}
                      </SpaceBetween>
                    )
                  }
                  return <ReactMarkdown remarkPlugins={[remarkGfm]}>{editedContent}</ReactMarkdown>
                })()
              )}
            </Container>

            {(selectedItem.contentType ?? selectedItem.content_type) === 'blog_seo' && editedSeo && (
              <Container header={<Header variant="h3">SEO Metadata</Header>}>
                <SpaceBetween size="s">
                  <FormField
                    label={`Title (${editedSeo.meta_title.length}/60)`}
                    errorText={editedSeo.meta_title.length > 60 ? 'Exceeds 60 chars' : undefined}
                  >
                    <Input
                      value={editedSeo.meta_title}
                      onChange={({ detail }) => setEditedSeo({ ...editedSeo, meta_title: detail.value })}
                    />
                  </FormField>
                  <FormField
                    label={`Description (${editedSeo.meta_description.length}/160)`}
                    errorText={editedSeo.meta_description.length > 160 ? 'Exceeds 160 chars' : undefined}
                  >
                    <Textarea
                      value={editedSeo.meta_description}
                      onChange={({ detail }) => setEditedSeo({ ...editedSeo, meta_description: detail.value })}
                      rows={3}
                    />
                  </FormField>
                  <FormField label="Slug">
                    <Input
                      value={editedSeo.slug}
                      onChange={({ detail }) => setEditedSeo({ ...editedSeo, slug: detail.value })}
                    />
                  </FormField>
                </SpaceBetween>
              </Container>
            )}

            {isEmailType(selectedItem.contentType ?? selectedItem.content_type) && (selectedItem.status === 'draft' || selectedItem.status === 'pending') && (
              <FormField label="Send to">
                <Input
                  type="email"
                  placeholder="recipient@example.com"
                  value={overrideEmail}
                  onChange={({ detail }) => setOverrideEmail(detail.value)}
                />
              </FormField>
            )}

            {showScheduler && (
              <FormField
                label="Schedule for"
                description={`Your timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`}
              >
                <SpaceBetween direction="horizontal" size="s">
                  <DatePicker
                    value={scheduleDate}
                    onChange={({ detail }) => setScheduleDate(detail.value)}
                    placeholder="YYYY/MM/DD"
                    openCalendarAriaLabel={selectedDate => `Choose date${selectedDate ? `, selected date is ${selectedDate}` : ''}`}
                  />
                  <TimeInput
                    value={scheduleTime}
                    onChange={({ detail }) => setScheduleTime(detail.value)}
                    placeholder="hh:mm"
                    format="hh:mm"
                  />
                  <Button onClick={handleSchedule} loading={scheduleLoading}>Schedule</Button>
                  <Button variant="link" onClick={() => setShowScheduler(false)}>Cancel</Button>
                </SpaceBetween>
              </FormField>
            )}

          </SpaceBetween>
        </Modal>
      )}

      {showRejectModal && selectedItem && (
        <Modal
          visible
          onDismiss={() => setShowRejectModal(false)}
          header="Reject item"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setShowRejectModal(false)}>Cancel</Button>
                <Button
                  variant="primary"
                  iconName="status-negative"
                  loading={rejectLoading}
                  onClick={handleReject}
                >
                  Confirm Rejection
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <Box variant="p">
              Reject this item? You can add optional feedback to record why.
            </Box>
            <FormField label="Rejection feedback (optional)">
              <Textarea
                value={rejectFeedback}
                onChange={({ detail }) => setRejectFeedback(detail.value)}
                placeholder="Explain why this content is being rejected..."
                rows={4}
              />
            </FormField>
          </SpaceBetween>
        </Modal>
      )}

      {deleteConfirmItem && (
        <Modal
          visible
          onDismiss={() => setDeleteConfirmItem(null)}
          header="Delete item"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setDeleteConfirmItem(null)}>Cancel</Button>
                <Button
                  variant="primary"
                  loading={deleteLoading}
                  onClick={async () => {
                    try {
                      await deleteItem(clientId, deleteConfirmItem.id)
                      setDeleteConfirmItem(null)
                    } catch (err) {
                      console.error('Delete failed:', err)
                    }
                  }}
                >
                  Delete
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <Box variant="p">
              Are you sure you want to delete this item? This action cannot be undone.
            </Box>
            <Alert type="warning" statusIconAriaLabel="Warning">
              The content will be permanently removed from the queue.
            </Alert>
          </SpaceBetween>
        </Modal>
      )}

      {showClearConfirm && (
        <Modal
          visible
          onDismiss={() => setShowClearConfirm(false)}
          header="Clear entire queue"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setShowClearConfirm(false)}>Cancel</Button>
                <Button
                  variant="primary"
                  loading={clearLoading}
                  onClick={async () => {
                    try {
                      await clearQueue(clientId, ['pending', 'draft', 'waiting_approval', 'rejected', 'failed'])
                      setShowClearConfirm(false)
                    } catch (err) {
                      console.error('Clear queue failed:', err)
                    }
                  }}
                >
                  Clear All
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <Box variant="p">
              Are you sure you want to clear the queue? This will permanently delete all pending, rejected, and failed items.
            </Box>
            <Alert type="warning" statusIconAriaLabel="Warning">
              Published and scheduled items will not be affected.
            </Alert>
          </SpaceBetween>
        </Modal>
      )}
    </SpaceBetween>
  )
}
